import React, { useState } from 'react';
import { getMonday, getFriday, toDateString, pad } from '../../services/dateUtils';

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

interface WeekNavPillProps {
  weekStart: string; // YYYY-MM-DD (Monday)
  onWeekChange: (monday: string, friday: string) => void;
  allowFuture?: boolean;
}

/**
 * The semi-transparent green pill containing [◀  Mon X – Fri Y  ▶  Today].
 * Single source of truth for the week selector — used by WeekNavigator's
 * default and pill variants AND inlined directly in the Posts toolbar
 * contextual pill, so the control is visually and behaviourally identical
 * everywhere.
 */
export const WeekNavPill: React.FC<WeekNavPillProps> = ({ weekStart, onWeekChange, allowFuture = false }) => {
  const currentMonday = toDateString(getMonday(new Date()));
  const blockNext = !allowFuture && weekStart >= currentMonday;
  const showTodayButton = weekStart !== currentMonday;

  const navigate = (direction: -1 | 1) => {
    const date = new Date(weekStart + 'T00:00');
    date.setDate(date.getDate() + direction * 7);
    onWeekChange(toDateString(getMonday(date)), toDateString(getFriday(date)));
  };
  const goToday = () => {
    const today = new Date();
    onWeekChange(toDateString(getMonday(today)), toDateString(getFriday(today)));
  };

  const fmt = (d: Date, includeYear: boolean) => {
    const wd = WEEKDAY_SHORT[d.getDay()];
    const dom = pad(d.getDate());
    const mon = MONTH_SHORT[d.getMonth()];
    return includeYear
      ? `${wd} ${dom} ${mon} ${d.getFullYear()}`
      : `${wd} ${dom} ${mon}`;
  };
  const mondayDate = new Date(weekStart + 'T00:00');
  const fridayDate = getFriday(mondayDate);
  const monLabel = fmt(mondayDate, false);
  const friLabel = fmt(fridayDate, true);

  return (
    <div className="week-nav-pill" role="group" aria-label="Week navigation" data-help="period-nav">
      <button
        type="button"
        className="week-nav-pill__chev"
        onClick={() => navigate(-1)}
        title="Previous week"
        aria-label="Previous week"
      >
        <i className="fas fa-chevron-left" />
      </button>
      <span className="week-nav-pill__label">{monLabel} – {friLabel}</span>
      <button
        type="button"
        className="week-nav-pill__chev"
        onClick={() => navigate(1)}
        disabled={blockNext}
        title={blockNext ? 'Already on current week' : 'Next week'}
        aria-label="Next week"
      >
        <i className="fas fa-chevron-right" />
      </button>
      {showTodayButton && (
        <button
          type="button"
          className="week-nav-pill__today"
          onClick={goToday}
          title="Jump to current week"
        >
          <i className="fas fa-calendar-day" /> Today
        </button>
      )}
    </div>
  );
};

interface WeekNavigatorProps {
  weekStart: string; // YYYY-MM-DD (Monday)
  onWeekChange: (monday: string, friday: string) => void;
  onReload?: () => Promise<void>;
  bare?: boolean; // suppresses own background/border when embedded in a toolbar
  /** When true, the "next week" button is never disabled. Used by the
   *  weekly schedule for forward planning. Default behaviour (Reports)
   *  blocks navigation past the current week. */
  allowFuture?: boolean;
  /** Visual variant. 'default' = legacy gray-bar; 'pill' = dark-green
   *  floating pill, matching the new top-tab page treatment. */
  variant?: 'default' | 'pill';
}

const WeekNavigator: React.FC<WeekNavigatorProps> = ({ weekStart, onWeekChange, onReload, bare, allowFuture = false, variant = 'default' }) => {
  const [spinning, setSpinning] = useState(false);

  const handleReload = async () => {
    if (!onReload) return;
    setSpinning(true);
    try {
      await onReload();
    } finally {
      setSpinning(false);
    }
  };

  if (variant === 'pill') {
    return (
      <div className="pill-toolbar pill-toolbar--week" role="toolbar" aria-label="Week navigation">
        <div className="pill-toolbar__side pill-toolbar__side--left" />
        <div className="pill-toolbar__center">
          <WeekNavPill weekStart={weekStart} onWeekChange={onWeekChange} allowFuture={allowFuture} />
        </div>
        <div className="pill-toolbar__side pill-toolbar__side--right">
          {onReload && (
            <button
              className="pill-toolbar__icon-btn"
              onClick={handleReload}
              disabled={spinning}
              title="Reload posts"
              aria-label="Reload posts"
            >
              <i className="fas fa-sync-alt" style={spinning ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="week-navigator" style={bare ? { background: 'none', borderBottom: 'none', padding: 0, minHeight: 0 } : undefined}>
      <div style={{ flex: 1 }} />
      <WeekNavPill weekStart={weekStart} onWeekChange={onWeekChange} allowFuture={allowFuture} />
      <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
        {onReload && (
          <button
            className="week-nav-btn"
            onClick={handleReload}
            disabled={spinning}
            title="Reload posts"
          >
            <i className="fas fa-sync-alt" style={spinning ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
          </button>
        )}
      </div>
    </div>
  );
};

export default WeekNavigator;
